import { UcoastEl } from '@/scripts/core/UcoastEl'
import { formatPhoneNumber } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { ModalDialog } from '@/scripts/theme/modal-dialog'

export class WaitlistForm extends UcoastEl {
	static htmlSelector = 'waitlist-form'
	private static workerUrl =
		'https://pvolve-klaviyo-bis-worker.refactorthis.workers.dev/subscribe'

	form: HTMLFormElement
	submitButton: HTMLButtonElement
	emailInput: HTMLInputElement
	variantInput: HTMLInputElement
	phoneInput?: HTMLInputElement
	errorMessageEl?: HTMLElement
	successMessageEl?: HTMLElement
	buttonText: string

	constructor() {
		super()
		this.form = q.rs('form', this)
		this.submitButton = q.rs('[type="submit"]', this)
		this.variantInput = q.rs('[name="variant"]', this)
		this.emailInput = q.rs('[type="email"]', this)
		this.phoneInput = q.os('[name="phone_number"]', this) ?? undefined
		this.errorMessageEl = q.os('[data-error-message]', this) ?? undefined
		this.successMessageEl =
			q.os('[data-success-message]', this) ?? undefined
		this.buttonText = this.getAttribute('data-button-text') || 'NOTIFY ME'
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
	}

	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	private getEnvironment(): string {
		const hostname = window.location.hostname
		return hostname === 'pvolve.com' || hostname === 'www.pvolve.com'
			? 'production'
			: 'staging'
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return

		const email = this.emailInput.value.trim()
		if (!email) return
		if (!this.isValidEmail(email)) {
			this.showError('Please enter a valid email address')
			return
		}

		this.setLoadingState(true)

		const variantId = this.variantInput.value

		const payload: Record<string, string> = {
			email,
			productId: '',
			variantId,
			productTitle: '',
			variantTitle: '',
			productUrl: window.location.href,
			environment: this.getEnvironment(),
		}

		if (this.phoneInput) {
			const phone = this.phoneInput.value.trim()
			if (phone) {
				payload.phone_number = formatPhoneNumber(phone)
			}
		}

		fetch(WaitlistForm.workerUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})
			.then(async (response) => {
				let result
				try {
					result = await response.json()
				} catch {
					throw new Error('Invalid response from server')
				}
				if (!response.ok || !result.success) {
					throw new Error(
						result.error || result.message || 'Something went wrong'
					)
				}
			})
			.then(() => {
				this.showSuccess()
				window.setTimeout(() => {
					q.os<ModalDialog>('#WaitlistModal')?.hide()
				}, 5000)
			})
			.catch((e) => {
				console.error('[WaitlistForm] Submit failed:', e)
				this.showError('Something went wrong. Please try again.')
			})
			.finally(() => {
				this.setLoadingState(false)
			})
	}

	private setLoadingState(isLoading: boolean) {
		if (isLoading) {
			this.submitButton.setAttribute('aria-disabled', 'true')
			this.submitButton.classList.add('loading')
		} else {
			this.submitButton.removeAttribute('aria-disabled')
			this.submitButton.classList.remove('loading')
		}
	}

	private showSuccess() {
		this.emailInput.value = ''
		if (this.phoneInput) this.phoneInput.value = ''
		this.form.classList.add('hidden')
		if (this.successMessageEl) {
			this.successMessageEl.classList.remove('hidden')
		}
	}

	private showError(message: string) {
		if (this.errorMessageEl) {
			this.errorMessageEl.textContent = message
			this.errorMessageEl.classList.remove('hidden')
		}
		window.setTimeout(() => {
			if (this.errorMessageEl) {
				this.errorMessageEl.classList.add('hidden')
			}
		}, 5000)
	}
}
